import { useEffect, useState } from "react";

import { haalBijlageUrl, haalBijlagen, type AfstandBijlage } from "./afstandApi";

/**
 * Wat de eigenaar al naar het asiel heeft gestuurd, om terug te zien.
 *
 * <p><b>Een bestandsnaam zegt hem niets.</b> Hij voegt een foto van zijn hond toe en wil die foto
 * zien staan, niet "IMG_4821.jpg" (Maarten, 27-10-2026). Daarom halen we van elke afbeelding ook de
 * bytes op; van een pdf of verslag blijft het bij de naam, want daar valt niets te tonen.
 *
 * <p><b>Via de sessie en niet rechtstreeks in een {@code src}.</b> De bijlagen liggen bij het asiel,
 * achter zijn login, en de eigenaar heeft daar geen account. Het platform geeft ze door op zijn
 * sessiekopregel -- die kan een {@code <img>} niet meesturen, dus maken we er een url van dit
 * tabblad van en geven die weer vrij zodra hij niet meer nodig is.
 */
export interface VerstuurdBestand {
  documentId: string;
  /** Het hoeveelste dier, in de volgorde waarin ze zijn weggeschreven; leeg = bij de aanvraag. */
  dierVolgnummer: number | null;
  soort: string | null;
  naam: string;
  /** Een url om hem te tonen; alleen gevuld voor afbeeldingen. */
  src: string | null;
}

export function useVerstuurdeBijlagen(
  aanvraagId: string | null | undefined,
  token: string,
  /** Verandert zodra er iets verstuurd kan zijn; dan halen we de lijst opnieuw op. */
  ronde: unknown,
): VerstuurdBestand[] {
  const [bestanden, setBestanden] = useState<VerstuurdBestand[]>([]);

  useEffect(() => {
    if (!aanvraagId) {
      setBestanden([]);
      return;
    }
    let levend = true;
    const gemaakt: string[] = [];
    const opruimen = () => {
      for (const url of gemaakt) URL.revokeObjectURL(url);
    };

    void (async () => {
      const koppen: AfstandBijlage[] = await haalBijlagen(aanvraagId, token).catch(() => []);
      const uit: VerstuurdBestand[] = [];
      for (const kop of koppen) {
        if (!levend) break;
        let src: string | null = null;
        if ((kop.contentType ?? "").startsWith("image/")) {
          src = await haalBijlageUrl(aanvraagId, kop.documentId, token).catch(() => null);
          if (src) gemaakt.push(src);
        }
        uit.push({
          documentId: kop.documentId,
          dierVolgnummer: kop.dierVolgnummer,
          soort: kop.soort,
          naam: kop.bestandsnaam,
          src,
        });
      }
      if (levend) {
        setBestanden(uit);
      } else {
        opruimen();
      }
    })();

    return () => {
      levend = false;
      opruimen();
    };
  }, [aanvraagId, token, ronde]);

  return bestanden;
}

/** De foto's die bij één dier horen; op volgnummer, want het platform kent de dier-id's niet. */
export function fotosVanDier(bestanden: VerstuurdBestand[], volgnummer: number): VerstuurdBestand[] {
  if (volgnummer < 0) {
    return [];
  }
  return bestanden.filter((b) => b.dierVolgnummer === volgnummer && b.src != null);
}

/** Alles wat geen foto van een dier is: paspoorten, verslagen, het entingenboekje. */
export function overigeBestanden(bestanden: VerstuurdBestand[]): VerstuurdBestand[] {
  return bestanden.filter((b) => b.soort !== "DIERFOTO");
}
